import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  fetchUnreadCount,
  fetchNotifications,
  markRead,
  markAllRead,
  setPollingPaused,
} from "../redux/slices/notificationsSlice";
import PushSettings from "./PushSettings";

// NOTIFY-F003/F004 (docs/TRD/task-notifications-contract.md §7, source TRD §3.8, §4.3).
// A 60s unread-count poll is cheap enough to run per open tab, but every hidden tab of
// every signed-in user adds up (TR9), so a hidden tab must make zero requests.
const POLL_INTERVAL_MS = 60000;

// A visibility-resume refetch is skipped when the last successful poll (mount, interval
// tick, or a prior resume) landed less than this long ago — rapid tab switching would
// otherwise fire a request per flap (TR9).
const POLL_RESUME_DEBOUNCE_MS = 5000;

const BellIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
    />
  </svg>
);

const formatBadge = (count) => (count > 9 ? "9+" : String(count));

const formatTriggerLabel = (count) =>
  count > 0 ? `Notifications, ${count} unread` : "Notifications, no unread notifications";

const formatAnnouncement = (count) =>
  count > 0 ? `${count} unread notification${count === 1 ? "" : "s"}` : "No unread notifications";

const NotificationBell = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const unreadCount = useSelector((state) => state.notifications.unreadCount);
  const items = useSelector((state) => state.notifications.items);

  const [isOpen, setIsOpen] = useState(false);
  // Seeded from the current count rather than "" so the mount run of the announcement
  // effect below (all effects run once on mount) sets the same value it already holds —
  // React bails out of the no-op update, the live region's text never actually mutates,
  // and a screen reader has nothing to announce until the count genuinely changes (AC-T9).
  const [announcement, setAnnouncement] = useState(() => formatAnnouncement(unreadCount));
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  // Polling (AC-F4.6): the interval itself is never torn down and recreated — only the
  // tick's own effect is skipped while hidden — because recreating a 60s interval on every
  // visibility flap is exactly the bug this pattern usually ships with (contract §3.3).
  useEffect(() => {
    dispatch(fetchUnreadCount());

    const handleVisibilityChange = () => {
      const hidden = document.visibilityState === "hidden";
      dispatch(setPollingPaused(hidden));
      if (hidden) {
        return;
      }
      // Read the live store value rather than a hook-selected one so this handler
      // (registered once, on mount) always sees the most recent poll — including one
      // from a prior resume — without needing to be torn down and re-registered on
      // every fetch (TR9 debounce).
      const { lastPolledAt } = store.getState().notifications;
      const polledRecently =
        lastPolledAt != null && Date.now() - lastPolledAt < POLL_RESUME_DEBOUNCE_MS;
      if (!polledRecently) {
        dispatch(fetchUnreadCount());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const intervalId = setInterval(() => {
      if (document.visibilityState === "hidden") {
        return;
      }
      dispatch(fetchUnreadCount());
    }, POLL_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [dispatch, store]);

  // NOTIFY-F006 message protocol (public/service-worker.js): the SW holds no bearer
  // token, so a `notificationclick` posts `{ type: 'notification-click', id }` to the
  // focused/opened client instead of calling the mark-read endpoint itself, and this app
  // dispatches the actual (optimistic, authenticated) markRead thunk on its behalf.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return undefined;
    }
    const handleMessage = (event) => {
      if (event.data?.type === "notification-click" && event.data.id != null) {
        dispatch(markRead(event.data.id));
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [dispatch]);

  // Live region text only changes when the count itself changes (this effect's only
  // dependency), so an unchanged poll never re-touches the DOM node and never re-announces
  // — a screen reader announcing "3 unread" every 60s regardless of change is a denial of
  // service on its own (AC-T9).
  useEffect(() => {
    setAnnouncement(formatAnnouncement(unreadCount));
  }, [unreadCount]);

  // Escape-to-close-and-refocus and click-outside-to-close, scoped to while the panel is
  // actually open so no listener sits on `document` for the component's whole lifetime.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Dispatch happens here in the handler, not inside a setIsOpen functional updater —
  // React may invoke that updater during its render phase, and a store dispatch from
  // there triggers a synchronous "Cannot update a component while rendering a different
  // component" re-entrancy warning (and, in a concurrent render, could run twice).
  const handleToggle = useCallback(() => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      dispatch(fetchNotifications({ limit: 10, offset: 0 }));
    }
  }, [isOpen, dispatch]);

  // A native <button> already opens on Enter/Space via the browser's own default action,
  // but that default is unreliable across test environments, so it is made explicit here.
  // preventDefault stops the browser from *also* firing its own synthesized click for the
  // same keypress, which would otherwise immediately toggle the panel shut again.
  const handleTriggerKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleToggle();
      }
    },
    [handleToggle]
  );

  const handleItemClick = useCallback(
    (item) => {
      // Optimistic mark-read first (AC-F4.4): the badge and this row's read state update
      // immediately, then navigation follows — never the other way around, and never via
      // window.location, which would defeat the optimistic update with a full reload.
      // Only dispatched for a still-unread item: an already-read row has nothing to mark,
      // and dispatching anyway risks the `rejected` reducer flipping it back to unread
      // (and re-incrementing the badge) if that row's mark-read call ever 404s.
      if (!item.read_at) {
        dispatch(markRead(item.id));
      }
      setIsOpen(false);
      navigate(item.deep_link);
    },
    [dispatch, navigate]
  );

  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllRead());
  }, [dispatch]);

  const showBadge = unreadCount > 0;

  return (
    <div className="wt-notif-bell" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`wt-notif-trigger${isOpen ? " is-open" : ""}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={formatTriggerLabel(unreadCount)}
        onClick={handleToggle}
        onKeyDown={handleTriggerKeyDown}
      >
        <BellIcon />
        {showBadge && (
          <span className="wt-notif-badge" aria-hidden="true">
            {formatBadge(unreadCount)}
          </span>
        )}
      </button>

      {/* Announces only on an actual count change (see the effect above); never colour
          alone, and independent of the badge, which is aria-hidden. */}
      <div className="visually-hidden" role="status">
        {announcement}
      </div>

      {isOpen && (
        <div className="wt-notif-panel">
          <div className="wt-notif-panel-header">
            <span className="wt-notif-panel-title">Notifications</span>
            <button
              type="button"
              className="wt-notif-markall"
              aria-label="Mark all read"
              onClick={handleMarkAllRead}
              disabled={!showBadge}
            >
              Mark all read
            </button>
          </div>

          {items.length === 0 ? (
            <p className="wt-notif-empty">You&apos;re all caught up</p>
          ) : (
            <ul className="wt-notif-list" aria-label="Notifications">
              {items.slice(0, 10).map((item) => {
                const isUnread = !item.read_at;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`wt-notif-item${isUnread ? " is-unread" : ""}`}
                      onClick={() => handleItemClick(item)}
                    >
                      {isUnread && <span className="wt-notif-dot" aria-hidden="true" />}
                      <span className="wt-notif-item-text">
                        <span className="wt-notif-item-title">{item.title}</span>
                        <span className="wt-notif-item-body">{item.body}</span>
                        <span className="wt-notif-item-time">
                          {moment(item.created_at).fromNow()}
                        </span>
                      </span>
                      {isUnread && <span className="visually-hidden"> Unread</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* NOTIFY-F007 (P1). PushSettings owns its own supported/enabled/permission
              checks and renders nothing at all when push is unsupported or disabled
              (AC-F6.6, AC-F6.8). */}
          <PushSettings />
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
